import { ContactTypeIcon } from "@/components/contact/ContactTypeIcon";
import AntIcon from "@/components/icons/AntIcon";
import { List } from "@/components/list/List";
import { CalendarPanel } from "@/components/views/CalendarPanel";
import CopyToClipboardView from "@/components/views/CopyToClipboardView";
import Ellipsis from "@/components/views/Ellipsis";
import { formatAggregatedName, formatPhoneNumber } from "@/utils/formatUtils";
import { cssVariables, dateToIsoDateString } from "@/utils/utils";
import { Avatar, Button, Col, Divider, Flex, Modal, Row, Space, Tooltip } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router";
import { useGetDashboardPersonalDates } from "../../../../../common/api/queries";
import { ModalSizes } from "../../../../../common/constants";
import { ContactType } from "../../../../../common/enums";
import { AGENT_ROUTE_PATHS } from "../../../../agent/paths";
import { CLIENT_ROUTE_PATHS } from "../../../../client/paths";

type Props = {
  isOpen: boolean;
  onCancel: VoidFunction;
};

export const DashboardPersonalDatesExtendedWidget = ({ isOpen, onCancel }: Props) => {
  const { t } = useTranslation();

  const [calendarDate, setCalendarDate] = useState<Dayjs>(() => dayjs());

  const { data, isFetching } = useGetDashboardPersonalDates({
    minDate: dateToIsoDateString(calendarDate.startOf("day")),
    maxDate: dateToIsoDateString(calendarDate.endOf("day")),
  });

  const getSelectedDayLabel = () => {
    return calendarDate.isSame(dayjs(), "day") ? t("common.today") : `${calendarDate.format("D. MMMM")}`;
  };

  const selectedDayPersons = data?.personalDates[dateToIsoDateString(calendarDate)] || [];

  return (
    <Modal
      width={ModalSizes.LARGE}
      open={isOpen}
      onCancel={onCancel}
      title={t("dashboard.personalDatesExtended.title")}
      footer={
        <Flex justify="space-between" align="center">
          <div>
            <span className="sub-header-info">{t("dashboard.personalDates.todayNameDays")}: </span>
            <b>{data?.todayNameDays.length ? data?.todayNameDays.join(", ") : "-"}</b>
          </div>
          <Button onClick={onCancel}>{t("common.close")}</Button>
        </Flex>
      }
    >
      <Row gutter={24}>
        <Col span={10}>
          <CalendarPanel value={calendarDate} onChange={setCalendarDate} data={data?.personalDates} />
        </Col>

        <Col span={14}>
          <h3>{t("dashboard.personalDatesExtended.helpers.personalDatesLabel", { day: getSelectedDayLabel() })}</h3>

          <List
            pagination={{
              pageSize: 3,
              size: "small",
              hideOnSinglePage: true,
              showQuickJumper: true,
              showSizeChanger: false,
            }}
            loading={isFetching}
            gap={12}
            split
            showEmpty
            style={{ height: 280 }}
            items={selectedDayPersons}
            rowKey={(item) => item.id}
            renderItem={(item) => (
              <Flex align="center" justify="space-between">
                <Flex align="start" gap={16} style={{ flex: 1, maxWidth: "100%" }}>
                  {item.clientType ? (
                    <Tooltip title={t("dashboard.personalDatesExtended.helpers.client")}>
                      <Avatar style={{ backgroundColor: cssVariables.colorOrange }} icon={<AntIcon type="team" />} />
                    </Tooltip>
                  ) : (
                    <Tooltip title={t("dashboard.personalDatesExtended.helpers.agent")}>
                      <Avatar
                        style={{ backgroundColor: cssVariables.colorYellow }}
                        icon={<AntIcon type="user-switch" />}
                      />
                    </Tooltip>
                  )}

                  <div style={{ flex: "1 0", width: 0 }}>
                    <h4 style={{ margin: "0 0 var(--ant-margin-xxs)" }}>
                      <Ellipsis>
                        <Link
                          className="no-link-color"
                          to={generatePath(
                            item.clientType ? CLIENT_ROUTE_PATHS.detail.to : AGENT_ROUTE_PATHS.detail.to,
                            {
                              id: item.id,
                            },
                          )}
                          target="_blank"
                        >
                          {formatAggregatedName(item)}
                        </Link>
                      </Ellipsis>
                    </h4>

                    <div style={{ color: "var(--ant-color-text-description)" }}>
                      <Row>
                        {item.phone ? (
                          <>
                            <Col span={10}>
                              <CopyToClipboardView
                                content={item.phone}
                                style={{ width: "100%" }}
                                wrapper={
                                  <>
                                    <ContactTypeIcon type={ContactType.PHONE_NUMBER} />
                                    <Ellipsis style={{ maxWidth: "82%", display: "inline-block" }}>
                                      <span style={{ marginLeft: 8 }}>{formatPhoneNumber(item.phone)}</span>
                                    </Ellipsis>
                                  </>
                                }
                              ></CopyToClipboardView>
                            </Col>

                            <Col>
                              <Divider orientation="vertical" style={{ marginRight: 16 }} />
                            </Col>
                          </>
                        ) : undefined}

                        {item.email ? (
                          <Col span={12}>
                            <CopyToClipboardView
                              content={item.email}
                              style={{ width: "100%" }}
                              wrapper={
                                <>
                                  <ContactTypeIcon type={ContactType.EMAIL} />
                                  <Ellipsis style={{ maxWidth: "82%", display: "inline-block" }}>
                                    <span style={{ marginLeft: 8 }}>{item.email}</span>
                                  </Ellipsis>
                                </>
                              }
                            ></CopyToClipboardView>
                          </Col>
                        ) : undefined}
                      </Row>
                    </div>
                  </div>
                </Flex>

                <Space size="small">
                  {item.hasNameDay && (
                    <Tooltip title={t("dashboard.personalDates.nameDay")}>
                      <span>
                        <AntIcon size="huge" color="green" type="contact" />
                      </span>
                    </Tooltip>
                  )}

                  {item.hasBirthday && (
                    <Tooltip title={t("dashboard.personalDates.birthday")}>
                      <span>
                        <AntIcon size="huge" color="red" type="gift" />
                      </span>
                    </Tooltip>
                  )}
                </Space>
              </Flex>
            )}
          />
        </Col>
      </Row>
    </Modal>
  );
};
